import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import hubLayout from 'styles/css/hubLayout.module.css';
import { decryptAES256, deleteUser, getUserById, addHyphenMobile, putUser, sendTempPassword, getRecorderList } from 'api/api';
import { useRecoilState } from 'recoil';
import { isDarkAtom } from 'styles/atoms';
import Loading from 'components/common/Loading';
import { Recorder, User } from 'type/type';
import { formattedDateTime } from 'pages/utils/commonUtils';
import { PrimaryButton } from 'components/common/Button';

function UserEdit() {
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User>(Object);
  const [recorderList, setRecorderList] = useState<Recorder[]>([]);
  const [searchParams] = useSearchParams();
  const [darkAtom, setDarkAtom] = useRecoilState(isDarkAtom);
  // useForm
  const { register, formState: { errors, isValid } } = useForm<any>({
    mode: 'all',
  });

  const getUser = useCallback(async () => {
    setLoading(true);
    const response = await getUserById(userId || "");
    if (response.code === 200) {
      // 복호화
      const REACT_APP_SECRET_KEY = process.env.REACT_APP_SECRET_KEY!;
      const REACT_APP_SECRET_IV = process.env.REACT_APP_SECRET_IV!;
      response.data.mobile = decryptAES256(response.data.mobile, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!);
      setUser(response.data);
      const recorderRes = await getRecorderList(0, 0, { user_id: userId });
      if (recorderRes.code === 200) {
        setRecorderList(recorderRes.data || []);
      }
    } else {
      alert(response.message);
    }
    setLoading(false);
  }, [userId]);

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setUser((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const _sendTempPassword = async () => {
    const confirmRs = window.confirm('임시 비밀번호를 생성하면 비밀번호가 변경됩니다.\n계속하시겠습니까?');
    if (confirmRs) {
      const response = await sendTempPassword(user.id);
      if (response.code === 200) {
        alert('임시 비밀번호 발송이 완료되었습니다.');
      } else {
        alert(response.message);
      }
    }
  };

  const delUser = async () => {
    if (recorderList && recorderList.length) {
      return alert('장비가 등록되어있는 고객은 삭제가 불가능합니다.');
    }
    const confirmRs = window.confirm('정말 삭제하시겠습니까?');
    if (confirmRs) {
      const response = await deleteUser(user.idx);
      if (response.code === 200) {
        alert('삭제가 완료되었습니다.');
        window.opener.parentCallBack('userDel');
        window.close();
      } else {
        alert(response.message);
      }
    }
  };

  const updateUser = async () => {
    const confirmRs = window.confirm('정말 수정하시겠습니까?');
    if (confirmRs) {
      const response = await putUser(user);
      if (response.code === 200) {
        alert('수정이 완료되었습니다.');
        window.opener.parentCallBack('userEdit');
        window.close();
      } else {
        alert(response.message);
      }
    }
  };

  useEffect(() => {
    getUser();
  },[getUser]);
  // Dark mode 상태 설정
  useEffect(() => {
    const isDark = searchParams.get("isDark");
    setDarkAtom(isDark === 'true');
  }, [searchParams, setDarkAtom]);
  return (
    <>
      {loading ? <Loading /> :
        <>
          <div className={`${hubLayout.cnt_box}`}>
            <div className={`${hubLayout.acc__cnt}`}>
              <div className={`${hubLayout.sec_title}`}>
                <h2 style={{ color: darkAtom ? '#fff' : '' }}>고객 관리</h2>
                <PrimaryButton type="button" onClick={_sendTempPassword}>임시 비밀번호 발송</PrimaryButton>
              </div>
              <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_pink}`}>고객 정보</h2>
              <div className={`${hubLayout.acc_item_info}`}>
                <dl>
                  <dt style={{ width: '8rem' }}>ID</dt>
                  <dd>
                    <input type="text" className={`${errors.user_id && hubLayout.border_red}`} readOnly
                      placeholder="ID" value={user.id} 
                      {...register(`user_id`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                  </dd>
                </dl>
                <dl>
                  <dt style={{ width: '8rem' }}>이름</dt>
                  <dd>
                    <input type="text" className={`${errors.name && hubLayout.border_red}`} readOnly
                      placeholder="이름" value={user.name} 
                      {...register(`name`, {
                        required: true,
                      })}
                    />
                  </dd>
                </dl>
                <dl>
                  <dt style={{ width: '8rem' }}>연락처</dt>
                  <dd>
                    <input type="text" className={`${errors.mobile && hubLayout.border_red}`} maxLength={11} readOnly
                      placeholder="연락처" value={user.mobile} 
                      {...register(`mobile`, {
                        required: {
                          value: true,
                          message: '연락처를 입력하세요',
                        },
                      })}
                    />
                  </dd>
                </dl>
                <dl>
                  <dt style={{ width: '8rem' }}>E-MAIL</dt>
                  <dd>
                    <input type="text" className={`${errors.email && hubLayout.border_red}`} 
                      placeholder="E-MAIL" value={user.email} 
                      {...register(`email`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                  </dd>
                </dl>
                <dl>
                  <dt style={{ width: '8rem' }}>주소</dt>
                  <dd>
                    <input type="text" className={`${errors.address && hubLayout.border_red}`} 
                      placeholder="주소" value={user.address} 
                      {...register(`address`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                  </dd>
                </dl>
                <dl>
                  <dt style={{ width: '8rem' }}>상세주소</dt>
                  <dd>
                    <input type="text" className={`${errors.address_detail && hubLayout.border_red}`} 
                      placeholder="상세주소" value={user.address_detail} 
                      {...register(`address_detail`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                  </dd>
                </dl>
                <dl>
                  <dt style={{ width: '8rem' }}>가입일</dt>
                  <dd>
                    <input type="text" className={`${errors.create_date && hubLayout.border_red}`} readOnly
                      placeholder="가입일" value={formattedDateTime(user.create_date!)}
                      {...register(`create_date`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                  </dd>
                </dl>
                <dl>
                  <dt style={{ width: '8rem' }}>마지막 로그인</dt>
                  <dd>
                    <input type="text" className={`${errors.last_login_date && hubLayout.border_red}`} readOnly
                      placeholder="마지막 로그인" value={user.last_login_date ? formattedDateTime(user.last_login_date) : '로그인 기록 없음'} 
                      {...register(`last_login_date`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                  </dd>
                </dl>
              </div>
            </div>
            <div className={`${hubLayout.btn_group} ${hubLayout.fixed_bottom_title} ${darkAtom ? hubLayout.back_black : hubLayout.back_white}`}>
              <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.pink}`} onClick={delUser} >삭제</button>
              <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} onClick={updateUser} disabled={!isValid} >수정</button>
            </div>
          </div>
        </>
      }
    </>
  )
}

export default UserEdit;